import * as Sentry from "@sentry/react";
import { type ReactNode } from "react";

interface SentryErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  showDialog?: boolean;
}

const SentryErrorBoundary = ({
  children,
  fallback,
  showDialog = true,
}: SentryErrorBoundaryProps) => {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div className="flex min-h-screen items-center justify-center bg-gray-50">
          <div className="max-w-md rounded-lg bg-white p-6 shadow-lg">
            <div className="mb-4 flex items-center">
              <div className="mr-3 flex h-10 w-10 items-center justify-center rounded-full bg-red-100">
                <svg
                  className="h-6 w-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                  />
                </svg>
              </div>
              <h2 className="text-lg font-semibold text-gray-900">
                Something went wrong
              </h2>
            </div>
            <p className="mb-4 text-sm text-gray-600">
              An unexpected error occurred. The error has been reported and
              we're working to fix it.
            </p>
            {import.meta.env.MODE === "development" && (
              <details className="mb-4">
                <summary className="cursor-pointer text-sm font-medium text-gray-700">
                  Error Details
                </summary>
                <pre className="mt-2 overflow-auto rounded bg-gray-100 p-2 text-xs text-gray-800">
                  {error instanceof Error ? error.message : String(error)}
                  {"\n"}
                  {error instanceof Error ? error.stack : ""}
                </pre>
              </details>
            )}
            <div className="flex space-x-3">
              <button
                onClick={resetError}
                className="flex-1 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="flex-1 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Reload Page
              </button>
            </div>
          </div>
        </div>
      )}
      showDialog={showDialog}
      beforeCapture={(scope, _, errorInfo) => {
        scope.setTag("errorBoundary", true);
        scope.setContext(
          "errorInfo",
          errorInfo as unknown as Record<string, any>
        );
      }}
    >
      {fallback || children}
    </Sentry.ErrorBoundary>
  );
};

export default SentryErrorBoundary;
